import React, { FC, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import { Button, Stack } from '@mui/material';
import { CommentsCard } from '../comments/CommentsCard';
import { Thread, ThreadActions } from '../comments/CommentThread';
import { RichTextField, htmlIsEmpty } from '../components/richText';
import {
  ADD_COMMENT,
  ADD_COMMENT_REPLY,
  DELETE_COMMENT,
  DELETE_COMMENT_REPLY,
  PIN_COMMENT,
  UPDATE_COMMENT,
  UPDATE_COMMENT_REPLY,
  UPDATE_STATUS,
} from './dictionary.graphql';

type TermOption = { id: string; preferred?: string | null };

/** Comments on a term: usage advice, documented discussions and suggested changes. */
export const Comments: FC<{ term: TermOption; comments: Thread[]; onUpdate: () => Promise<unknown> }> = ({ term, comments, onUpdate }) => {
  const [composing, setComposing] = useState(false);
  const actions = useTermCommentActions();

  return (
    <CommentsCard
      comments={comments}
      actions={actions}
      onAdd={() => setComposing(true)}
      showExternalFilter={false}
      richText
      bare
      composer={
        composing && (
          <NewComment
            termId={term.id}
            onDone={async (added) => {
              setComposing(false);
              if (added) await onUpdate();
            }}
          />
        )
      }
    />
  );
};

function NewComment({ termId, onDone }: { termId: string; onDone: (added: boolean) => void }) {
  const [text, setText] = useState('');
  const [addComment, { loading }] = useMutation(ADD_COMMENT);

  const add = async () => {
    await addComment({ variables: { input: { termId, text } } });
    onDone(true);
  };

  return (
    <Stack spacing={1} sx={{ mb: 2 }}>
      <RichTextField value={text} onChange={setText} placeholder="Skriv en kommentar..." autoFocus />
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button onClick={() => onDone(false)}>Avbryt</Button>
        <Button disabled={loading || htmlIsEmpty(text)} onClick={add} variant="contained" disableElevation>
          {loading ? 'Lagrer...' : 'Legg til'}
        </Button>
      </Stack>
    </Stack>
  );
}

/** The term comment mutations, in the shape the shared comment thread UI expects. */
function useTermCommentActions(): ThreadActions {
  const [deleteComment] = useMutation(DELETE_COMMENT);
  const [updateStatus] = useMutation(UPDATE_STATUS);
  const [pinComment] = useMutation(PIN_COMMENT);
  const [updateComment] = useMutation(UPDATE_COMMENT);
  const [createReply] = useMutation(ADD_COMMENT_REPLY);
  const [updateReply] = useMutation(UPDATE_COMMENT_REPLY);
  const [deleteReply] = useMutation(DELETE_COMMENT_REPLY);

  return useMemo<ThreadActions>(
    () => ({
      remove: (commentId) =>
        deleteComment({
          variables: { input: { commentId } },
          update(cache) {
            cache.evict({ id: cache.identify({ __typename: 'TermCommentModel', id: commentId }) });
            cache.gc();
          },
        }),
      setStatus: (commentId, status) => updateStatus({ variables: { input: { commentId, status } } }),
      pin: (commentId, isPinned) => pinComment({ variables: { input: { commentId, isPinned } } }),
      update: (commentId, text) => updateComment({ variables: { input: { commentId, text } } }),
      createReply: (commentId, comment) => createReply({ variables: { input: { commentId, comment } } }),
      updateReply: (commentId, replyId, comment) => updateReply({ variables: { input: { commentId, replyId, comment } } }),
      deleteReply: (commentId, replyId) => deleteReply({ variables: { input: { commentId, replyId } } }),
    }),
    [deleteComment, updateStatus, pinComment, updateComment, createReply, updateReply, deleteReply],
  );
}
